import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from "@/components/ui/toaster"
import Layout from './components/Layout'
import AccessDenied from './components/AccessDenied'
import BillsPage from './pages/BillsPage'
import Jobs from './pages/Jobs.tsx'
import UsersPage from './pages/UsersPage'
import ConfigPage from './pages/ConfigPage'
import Costs from './pages/Costs.tsx'
import {useEffect, useState} from 'react'

// Import the OIDC web component to register it
import '@umd-dit/react-auth'


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData: unknown) => previousData,
      throwOnError: (error: Error) => {
        return error?.message?.includes('401') || error?.message?.includes('403')
      }
    },
    mutations: {
      retry: 1,
      retryDelay: 1000
    }
  }
})

function PreserveQueryParamsRoute() {
  const location = useLocation()
  return <Navigate to={`/bills${location.search}`} replace />
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAccessDenied, setIsAccessDenied] = useState(false)

  useEffect(() => {
    const handleAccessGranted = (event: CustomEvent) => {
      const { authenticated } = event.detail
      setIsAuthenticated(authenticated)
      setIsLoading(false)
    }

    const handleAccessDenied = () => {
      setIsAccessDenied(true)
    }

    document.addEventListener('access-granted', handleAccessGranted as EventListener)
    document.addEventListener('access-denied', handleAccessDenied as EventListener)

    return () => {
      document.removeEventListener('access-granted', handleAccessGranted as EventListener)
      document.removeEventListener('access-denied', handleAccessDenied as EventListener)
    }
  }, [])

  if (isAccessDenied) {
    return <AccessDenied />
  }

  console.log('Legislative Review Client ID is ' + import.meta.env.VITE_OIDC_CLIENT_ID)

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <umd-auth
          api-url={import.meta.env.VITE_API_URL}
          authority={import.meta.env.VITE_OIDC_AUTHORITY}
          client-id={import.meta.env.VITE_OIDC_CLIENT_ID}
          devmode={import.meta.env.VITE_OIDC_DEVMODE}
          debug-mode={import.meta.env.DEV ? "true" : "false"}
          id="oidc-auth-component"
        ></umd-auth>

        {isLoading ? (
          <div className="fixed inset-0 flex items-center justify-center bg-white/90">
            <div className="w-24 h-24 border-8 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        ) : isAuthenticated ? (
          <>
            <Routes>
              <Route path="/" element={<PreserveQueryParamsRoute/>}/>
              <Route element={<Layout/>}>
                <Route path="bills" element={<BillsPage/>}/>
                <Route path="users" element={<UsersPage/>}/>
                <Route path="jobs" element={<Jobs/>}/>
                <Route path="costs" element={<Costs/>}/>
                <Route path="config" element={<ConfigPage/>}/>
                <Route path="/" element={<BillsPage/>}/>
              </Route>
              <Route path="*" element={<Navigate to="/" replace/>}/>
            </Routes>
            <Toaster/>
          </>
        ):null}
      </QueryClientProvider>
    </Router>
  )
}